/**
 * GetQuote Component with Email Service Integration
 * 
 * This component uses the centralized email service for sending quote requests.
 * Email credentials are securely stored in environment variables.
 * 
 * Setup:
 * 1. Ensure .env file exists with EmailJS credentials
 * 2. See ENV_SETUP_INSTRUCTIONS.md for detailed setup guide
 * 3. Email service is automatically configured from environment variables
 */

import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  IconButton,
  Typography
} from '@mui/material';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { emailService } from '../../services/emailService';
import { trackQuoteRequest } from '../../utils/analytics';

interface QuoteFormData {
  name: string;
  email: string;
  phone: string;
  company?: string;
  serviceType: string;
  projectDescription: string;
  budget: string;
  timeline: string;
}

interface GetQuoteProps {
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  children?: React.ReactNode;
}

const GetQuote: React.FC<GetQuoteProps> = ({
  variant = 'contained',
  size = 'medium',
  className = '',
  children = 'Get Quote'
}) => {
  const [open, setOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<QuoteFormData>();

  const handleOpen = () => {
    setOpen(true);
    setSubmitStatus('idle');
  };

  const handleClose = () => {
    setOpen(false);
    reset();
    setSubmitStatus('idle');
  };

  const handleDialogClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    // Prevent closing on backdrop click or escape key
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handleClose();
  };

  const onSubmit = async (data: QuoteFormData) => {
    try {
      // Use the email service to send quote request
      const quoteData = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        company: data.company,
        projectDescription: data.projectDescription,
      };

      // Track quote request attempt
      trackQuoteRequest(quoteData.projectDescription || 'General', 'email_form');
      
      const result = await emailService.sendQuoteRequest(quoteData);

      if (result.success) {
        setSubmitStatus('success');
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
        // eslint-disable-next-line no-console
        console.error('Email service error:', result.message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sending quote request:', error);
      setSubmitStatus('error');
    }
  };



  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleOpen}
        sx={{
          backgroundColor: variant === 'contained' ? '#ffca28' : 'transparent',
          color: variant === 'contained' ? '#1a237e' : '#ffca28',
          borderRadius: '4px',
          padding: '10px 20px',
          textTransform: 'none',
          fontWeight: 600,
          border: variant === 'outlined' ? '2px solid #ffca28' : 'none',
          '&:hover': {
            backgroundColor: variant === 'contained' ? '#ffd54f' : 'rgba(255, 202, 40, 0.1)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(255, 202, 40, 0.3)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        {children}
      </Button>

      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 768}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: '12px' },
            maxHeight: { xs: '100vh', md: '90vh' },
            margin: { xs: 0, md: '32px' },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography 
              variant="h5" 
              component="h2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Request a Quote
            </Typography>
            <IconButton 
              onClick={handleClose} 
              size="small"
              sx={{ 
                backgroundColor: { xs: 'rgba(0,0,0,0.05)', md: 'transparent' },
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 1,
              fontSize: { xs: '0.875rem', md: '1rem' },
              lineHeight: 1.5
            }}
          >
            Tell us about your project and we'll get back to you with a customized quote.
          </Typography>
        </DialogTitle>

        <DialogContent 
          dividers
          sx={{ 
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2 },
            overflowY: 'auto',
            maxHeight: { xs: 'calc(100vh - 180px)', md: 'none' }
          }}
        >
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            {submitStatus === 'success' && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Your quote request has been sent successfully! We'll get back to you soon.
              </Alert>
            )}
            {submitStatus === 'error' && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                There was an error sending your request. Please try again.
              </Alert>
            )}

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr', 
              gap: { xs: 1.5, md: 2 }, 
              mb: 2 
            }}>
              <TextField
                fullWidth
                label="Full Name *"
                size={window.innerWidth < 768 ? "medium" : "medium"}
                {...register('name', { required: 'Name is required' })}
                error={!!errors.name}
                helperText={errors.name?.message}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: { xs: '1rem', md: '1rem' } 
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Email Address *"
                type="email"
                size={window.innerWidth < 768 ? "medium" : "medium"}
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: { xs: '1rem', md: '1rem' } 
                  }
                }}
              />

              <TextField
                fullWidth
                label="Phone Number *"
                size={window.innerWidth < 768 ? "medium" : "medium"}
                {...register('phone', {
                  required: 'Phone number is required',
                  pattern: {
                    value: /^(\+91|91)?[6-9]\d{9}$/,
                    message: 'Please enter a valid Indian mobile number (10 digits starting with 6-9)'
                  }
                })}
                error={!!errors.phone}
                helperText={errors.phone?.message || 'Enter your mobile number for quick contact'}
                placeholder="+91 XXXXXXXXXX"
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: { xs: '1rem', md: '1rem' } 
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Company Name"
                size={window.innerWidth < 768 ? "medium" : "medium"}
                {...register('company')}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: { xs: '1rem', md: '1rem' } 
                  }
                }}
              />

              <TextField
                fullWidth
                label="Project Description *"
                multiline
                rows={window.innerWidth < 768 ? 3 : 4}
                {...register('projectDescription', { required: 'Project description is required' })}
                error={!!errors.projectDescription}
                helperText={errors.projectDescription?.message || 'Please describe your project requirements in detail'}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: { xs: '1rem', md: '1rem' } 
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions 
          sx={{ 
            px: { xs: 2, md: 3 }, 
            py: { xs: 1.5, md: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}
        >
          <Button 
            onClick={handleClose} 
            variant="outlined"
            fullWidth={window.innerWidth < 600}
            sx={{
              borderColor: '#1a237e',
              color: '#1a237e',
              order: { xs: 2, sm: 1 },
              minHeight: { xs: '48px', md: '36px' },
              fontSize: { xs: '1rem', md: '0.875rem' },
              '&:hover': {
                borderColor: '#1a237e',
                backgroundColor: 'rgba(26, 35, 126, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            fullWidth={window.innerWidth < 600}
            sx={{
              backgroundColor: '#ffca28',
              color: '#1a237e',
              order: { xs: 1, sm: 2 },
              ml: { xs: 0, sm: 1 },
              minHeight: { xs: '48px', md: '36px' },
              fontSize: { xs: '1rem', md: '0.875rem' },
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#ffd54f',
              },
            }}
            disabled={submitStatus === 'success'}
          >
            {submitStatus === 'success' ? 'Sent!' : 'Send Quote Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GetQuote;
